import { create } from 'zustand';
import {
    DateTimeMode,
    DEFAULT_CONFIDENCE_THRESHOLD,
    type QueryFilters,
} from '@/utils/query-filters';

export interface QueryFiltersState {
    filters: QueryFilters;
    selectedAlertRuleIds: Set<string>;
    selectedObjectClassIds: Set<number>;
    setDateTimeFilter: (
        mode: DateTimeMode,
        customRange?: { startDate: Date; endDate: Date },
    ) => void;
    clearDateTimeFilter: () => void;
    setConfidenceFilter: (enabled: boolean, min?: number, max?: number) => void;
    setSceneOnly: (enabled: boolean) => void;
    resetFilters: () => void;
    toggleAlertRuleSelection: (alertRuleId: string) => void;
    clearAlertRuleSelection: () => void;
    setAlertRuleSelection: (alertRuleIds: Set<string>) => void;
    getSelectedAlertRuleIds: () => string[];
    setObjectClassSelection: (classIds: Set<number>) => void;
    clearObjectClassSelection: () => void;
    getSelectedObjectClassIds: () => number[];
}

const DEFAULT_FILTERS: QueryFilters = {
    dateTime: {
        mode: DateTimeMode.ALL,
    },
    confidence: {
        enabled: false,
        min: DEFAULT_CONFIDENCE_THRESHOLD,
        max: 100,
    },
    sceneOnly: false,
};

export const useQueryFiltersStore = create<QueryFiltersState>()((set, get) => ({
    filters: DEFAULT_FILTERS,
    selectedAlertRuleIds: new Set<string>(),
    selectedObjectClassIds: new Set<number>(),
    setDateTimeFilter: (mode, customRange) => {
        set((state) => ({
            filters: {
                ...state.filters,
                dateTime: {
                    mode,
                    customRange:
                        mode === DateTimeMode.CUSTOM ? customRange : undefined,
                },
            },
        }));
    },
    clearDateTimeFilter: () => {
        set((state) => ({
            filters: {
                ...state.filters,
                dateTime: {
                    mode: DateTimeMode.ALL,
                    customRange: undefined,
                },
            },
        }));
    },
    setConfidenceFilter: (enabled, min = 0, max = 100) => {
        set((state) => ({
            filters: {
                ...state.filters,
                confidence: {
                    enabled,
                    min: enabled ? min : DEFAULT_CONFIDENCE_THRESHOLD,
                    max: enabled ? max : 100,
                },
            },
        }));
    },
    setSceneOnly: (enabled) => {
        set((state) => ({
            filters: { ...state.filters, sceneOnly: enabled },
        }));
    },
    resetFilters: () => {
        set({ filters: DEFAULT_FILTERS });
    },
    toggleAlertRuleSelection: (alertRuleId) => {
        set((state) => {
            const newSelected = new Set(state.selectedAlertRuleIds);
            if (newSelected.has(alertRuleId)) {
                newSelected.delete(alertRuleId);
            } else {
                newSelected.add(alertRuleId);
            }
            return { selectedAlertRuleIds: newSelected };
        });
    },
    clearAlertRuleSelection: () => {
        set({ selectedAlertRuleIds: new Set<string>() });
    },
    setAlertRuleSelection: (alertRuleIds) => {
        set({ selectedAlertRuleIds: new Set(alertRuleIds) });
    },
    getSelectedAlertRuleIds: () => {
        const { selectedAlertRuleIds } = get();
        return Array.from(selectedAlertRuleIds);
    },
    setObjectClassSelection: (classIds) => {
        set({ selectedObjectClassIds: new Set(classIds) });
    },
    clearObjectClassSelection: () => {
        set({ selectedObjectClassIds: new Set<number>() });
    },
    getSelectedObjectClassIds: () => {
        const { selectedObjectClassIds } = get();
        return Array.from(selectedObjectClassIds);
    },
}));
